import React from 'react';
import { Navigate } from 'react-router-dom';
import { useUserData } from '../hooks';

interface ProtectedRouteProps {
    children: React.ReactNode;
    requiredPermission: keyof ReturnType<typeof useUserData>['pages'];
    redirectTo?: string;
}

/**
 * ProtectedRoute - Protects routes based on user permissions
 *
 * Usage:
 * <Route path="/cms/users" element={
 *     <ProtectedRoute requiredPermission="users">
 *         <Users />
 *     </ProtectedRoute>
 * } />
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
    children,
    requiredPermission,
    redirectTo = '/cms'
}) => {
    const { pages, isLoading } = useUserData();

    // Show loading state while checking permissions
    if (isLoading) {
        return (
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '400px'
            }}>
                <div>Loading...</div>
            </div>
        );
    }

    // Check if user has permission for this page
    const hasPermission = pages[requiredPermission];

    if (!hasPermission) {
        // Redirect to dashboard or specified path if no permission
        return <Navigate to={redirectTo} replace />;
    }

    // User has permission, render the page
    return <>{children}</>;
};

export default ProtectedRoute;